import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
	AutoComplete,
	Button,
	Card,
	Col,
	Divider,
	Input,
	InputNumber,
	Modal,
	Popover,
	Popconfirm,
	Row,
	Select,
	Space,
	Table,
	Tag,
	Tooltip,
	Typography,
	message,
} from 'antd'
import {
	DownloadOutlined,
	EditOutlined,
	QuestionCircleOutlined,
	ReloadOutlined,
	ScheduleOutlined,
	SyncOutlined,
	ThunderboltOutlined,
	UploadOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import * as XLSX from 'xlsx'
import { useNavigate } from 'react-router-dom'
import GlobalHeader from '../../common/components/Header/GlobalHeader'
import { useTheme, useThemeColors } from '../../common/ThemeContext'
import { departmentService, leaveService } from '../../common/service'
import { staffService } from '../../common/service/staff.service'

const { Text } = Typography

interface StaffBalance {
	staffId: string
	name?: string
	year: number
	entitlements: Record<string, number>
	used: Record<string, number>
	remaining: Record<string, number>
	carryForward?: Record<string, number>
}

interface StaffOption {
	value: string
	label: string
	staffId: string
}

interface DepartmentOption {
	value: string
	label: string
}

const CURRENT_YEAR = new Date().getFullYear()
const YEARS = Array.from({ length: 8 }, (_, i) => CURRENT_YEAR - 2 + i)

const cardHelpIcon = (content: React.ReactNode) => (
	<Popover content={content} trigger='click' placement='leftTop'>
		<Button
			type='text'
			size='small'
			shape='circle'
			icon={<QuestionCircleOutlined />}
			aria-label='Open usage guide'
		/>
	</Popover>
)

interface ManageBalanceProps {
	embedded?: boolean
}

const ManageBalance: React.FC<ManageBalanceProps> = ({ embedded = false }) => {
	const { isDarkMode } = useTheme()
	const themeColors = useThemeColors()
	const { primary, text, textSecondary, bg, border, chartBg } = themeColors
	const navigate = useNavigate()

	const [year, setYear] = useState<number>(CURRENT_YEAR)
	const [staffSearch, setStaffSearch] = useState('')
	const [staffOptions, setStaffOptions] = useState<StaffOption[]>([])
	const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null)

	const [balance, setBalance] = useState<StaffBalance | null>(null)
	const [loading, setLoading] = useState(false)
	const [bulkLoading, setBulkLoading] = useState(false)
	const [syncLoading, setSyncLoading] = useState(false)

	// Edit modal
	const [editModalOpen, setEditModalOpen] = useState(false)
	const [editValues, setEditValues] = useState<Record<string, number>>({})
	const [saving, setSaving] = useState(false)

	// Departments
	const [departments, setDepartments] = useState<DepartmentOption[]>([])

	// Bulk download
	const [exportYear, setExportYear] = useState<number>(CURRENT_YEAR)
	const [exportDepartmentId, setExportDepartmentId] = useState<string | undefined>(undefined)
	const [exportLoading, setExportLoading] = useState(false)

	// Staff ID direct search
	const [staffIdInput, setStaffIdInput] = useState('')

	// Bulk upload
	const fileInputRef = useRef<HTMLInputElement>(null)
	const [importModalOpen, setImportModalOpen] = useState(false)
	const [importRecords, setImportRecords] = useState<Array<Record<string, any>>>([])
	const [importYear, setImportYear] = useState<number>(CURRENT_YEAR)
	const [importLoading, setImportLoading] = useState(false)

	// ── Fetch balance for selected staff ──────────────────────────────────
	const fetchBalance = useCallback(async () => {
		if (!selectedStaffId) return
		setLoading(true)
		try {
			const res = await leaveService.getBalance(selectedStaffId, year)
			const data = res?.data ?? res
			setBalance(data)
		} catch {
			message.error('Failed to load leave balance')
		} finally {
			setLoading(false)
		}
	}, [selectedStaffId, year])

	useEffect(() => {
		fetchBalance()
	}, [fetchBalance])

	// ── Fetch departments ──────────────────────────────────────────────────
	useEffect(() => {
		departmentService
			.fetchListing()
			.then((res: any) => {
				const list = res?.data ?? res ?? []
				const arr = Array.isArray(list) ? list : []
				setDepartments(
					arr.map((d: any) => ({
						value: d.id,
						label: d.nameEn || d.name || d.id,
					})),
				)
			})
			.catch(() => setDepartments([]))
	}, [])

	// ── Bulk download ──────────────────────────────────────────────────────
	const handleExportDownload = async () => {
		setExportLoading(true)
		try {
			const res: any = await leaveService.exportBalances({
				year: exportYear,
				departmentId: exportDepartmentId || undefined,
			})
			const rows: Array<Record<string, any>> = res?.data ?? res ?? []
			if (!rows.length) {
				message.warning('No data found for the selected filters')
				return
			}
			const ws = XLSX.utils.json_to_sheet(rows)
			const wb = XLSX.utils.book_new()
			XLSX.utils.book_append_sheet(wb, ws, 'Leave Balance')
			const deptLabel = exportDepartmentId
				? `_${departments.find((d) => d.value === exportDepartmentId)?.label ?? exportDepartmentId}`
				: '_all'
			XLSX.writeFile(wb, `leave_balance_${exportYear}${deptLabel}.xlsx`)
		} catch {
			message.error('Failed to export leave balances')
		} finally {
			setExportLoading(false)
		}
	}

	const handleDownloadTemplate = async () => {
		try {
			const res: any = await leaveService.getLeaveSettings()
			const settingsData = res?.data ?? res
			const types: string[] = (settingsData?.types ?? [])
				.map((t: any) => t.name)
				.filter(Boolean)
			if (!types.length) {
				message.warning('No leave types configured. Please set up leave types first.')
				return
			}
			const headers = ['staffId', 'staffName', 'department', ...types]
			const exampleRow: any[] = ['001', 'John Doe', 'Engineering', ...types.map(() => 0)]
			const ws = XLSX.utils.aoa_to_sheet([headers, exampleRow])
			const wb = XLSX.utils.book_new()
			XLSX.utils.book_append_sheet(wb, ws, 'Template')
			XLSX.writeFile(wb, 'leave_balance_template.xlsx')
		} catch {
			message.error('Failed to load leave types for template')
		}
	}

	// ── Bulk upload ────────────────────────────────────────────────────────
	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0]
		if (!file) return
		const reader = new FileReader()
		reader.onload = (evt) => {
			try {
				const data = evt.target?.result
				const wb = XLSX.read(data, { type: 'binary' })
				const ws = wb.Sheets[wb.SheetNames[0]]
				const rows: Array<Record<string, any>> = XLSX.utils.sheet_to_json(ws)
				if (!rows.length) {
					message.warning('The file has no data rows')
					return
				}
				setImportRecords(rows)
				setImportModalOpen(true)
			} catch {
				message.error('Failed to parse file. Please use the provided template.')
			}
		}
		reader.readAsBinaryString(file)
		// Reset so the same file can be re-selected
		e.target.value = ''
	}

	const handleConfirmImport = async () => {
		if (!importRecords.length) return
		setImportLoading(true)
		try {
			const res: any = await leaveService.importBalances({
				year: importYear,
				records: importRecords,
			})
			const result = res?.data ?? res
			message.success(
				`Import complete: ${result?.imported ?? '?'} updated, ${result?.errors ?? 0} errors`,
			)
			setImportModalOpen(false)
			setImportRecords([])
			if (selectedStaffId) fetchBalance()
		} catch {
			message.error('Failed to import leave balances')
		} finally {
			setImportLoading(false)
		}
	}

	// ── Staff autocomplete ─────────────────────────────────────────────────
	const handleStaffSearch = async (text: string) => {
		setStaffSearch(text)
		if (!text.trim()) {
			setStaffOptions([])
			return
		}
		try {
			const results = await staffService.autocomplete(text.trim())
			setStaffOptions(
				results.map((s) => ({
					value: s.name,
					label: s.name,
					staffId: s.id,
				})),
			)
		} catch {
			setStaffOptions([])
		}
	}

	// ── Balance columns ────────────────────────────────────────────────────
	interface BalanceRow {
		leaveType: string
		entitlement: number
		used: number
		remaining: number
		carryForward: number | null
	}

	const balanceRows: BalanceRow[] = balance
		? Object.keys(balance.entitlements).map((type) => ({
				leaveType: type,
				entitlement: balance.entitlements[type] ?? 0,
				used: balance.used[type] ?? 0,
				remaining: balance.remaining[type] ?? 0,
				carryForward: balance.carryForward ? (balance.carryForward[type] ?? null) : null,
			}))
		: []

	const columns: ColumnsType<BalanceRow> = [
		{
			title: 'Leave Type',
			dataIndex: 'leaveType',
			key: 'leaveType',
			render: (v: string) => <Text strong>{v}</Text>,
		},
		{
			title: 'Entitlement (days)',
			dataIndex: 'entitlement',
			key: 'entitlement',
			align: 'center',
		},
		{
			title: 'Used (days)',
			dataIndex: 'used',
			key: 'used',
			align: 'center',
		},
		{
			title: 'Remaining (days)',
			dataIndex: 'remaining',
			key: 'remaining',
			align: 'center',
			render: (v: number) => (
				<Tag color={v > 0 ? 'green' : v === 0 ? 'default' : 'red'}>{v}</Tag>
			),
		},
		{
			title: (
				<Tooltip title="Days rolled over from the previous year into this year's entitlement">
					<span>
						Carry Forward (days){' '}
						<QuestionCircleOutlined style={{ fontSize: 12, color: '#64748b' }} />
					</span>
				</Tooltip>
			),
			dataIndex: 'carryForward',
			key: 'carryForward',
			align: 'center',
			render: (v: number | null) =>
				v === null ? (
					<Tag color='default'>—</Tag>
				) : (
					<Tag color={v > 0 ? 'blue' : 'default'}>{v}</Tag>
				),
		},
	]

	// ── Save entitlements ──────────────────────────────────────────────────
	const handleOpenEdit = () => {
		if (!balance) return
		setEditValues({ ...balance.entitlements })
		setEditModalOpen(true)
	}

	const handleSaveEntitlements = async () => {
		if (!selectedStaffId || !balance) return
		setSaving(true)
		try {
			await leaveService.setBalance(selectedStaffId, { year, ...editValues })
			message.success('Leave balance updated')
			setEditModalOpen(false)
			fetchBalance()
		} catch {
			message.error('Failed to save leave balance')
		} finally {
			setSaving(false)
		}
	}

	// ── Bulk apply defaults ────────────────────────────────────────────────
	const handleBulkApply = async () => {
		setBulkLoading(true)
		try {
			const res = await leaveService.bulkSetBalance({ year })
			const result = res?.data ?? res
			message.success(
				`Default entitlements applied to ${result?.updated ?? '?'} staff members for ${year}`,
			)
		} catch {
			message.error('Failed to apply default entitlements')
		} finally {
			setBulkLoading(false)
		}
	}

	// ── Sync settings to existing docs ────────────────────────────────────
	const handleSyncDefaults = async () => {
		setSyncLoading(true)
		try {
			const res = await leaveService.syncBalanceDefaults(year)
			const result = res?.data ?? res
			message.success(
				`Synced default entitlements to ${result?.synced ?? '?'} balance documents for ${year}`,
			)
			if (selectedStaffId) fetchBalance()
		} catch {
			message.error('Failed to sync default entitlements')
		} finally {
			setSyncLoading(false)
		}
	}

	return (
		<div style={{ minHeight: embedded ? 'auto' : '100vh', background: chartBg }}>
			{!embedded && <GlobalHeader />}

			{/* ── Page Header (inherited from /staff design) ── */}
			<div
				style={{
					background: bg,
					borderBottom: `1px solid ${border}`,
					padding: '14px 24px',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
				}}
			>
				<div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
					<div
						style={{
							width: 48,
							height: 48,
							borderRadius: 10,
							background: isDarkMode ? `${primary}15` : primary,
							color: isDarkMode ? primary : '#fff',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							flexShrink: 0,
						}}
					>
						<ScheduleOutlined style={{ fontSize: 24 }} />
					</div>
					<div>
						<div
							style={{
								fontSize: 20,
								fontWeight: 700,
								color: text,
								lineHeight: '1.2',
							}}
						>
							Leave Balance
						</div>
						<div style={{ fontSize: 12, color: textSecondary, marginTop: 2 }}>
							Manage leave entitlements and balances for staff members
						</div>
					</div>
				</div>
				<Space>
					<Button onClick={() => navigate('/leaves/carry-forward-history')}>
						Carry Forward History
					</Button>
					{!embedded ? (
						<Button onClick={() => navigate('/leaves')}>← Back to Leaves</Button>
					) : null}
				</Space>
			</div>

			<div style={{ padding: '20px 24px' }}>
				{/* ── Section 1: Year & Bulk Operations ── */}
				<Card
					style={{
						marginBottom: 16,
						borderRadius: 8,
						boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
					}}
					styles={{ body: { padding: '16px 20px' } }}
					title={<Text strong>Year &amp; Bulk Operations</Text>}
					extra={cardHelpIcon(
						<div style={{ maxWidth: 320 }}>
							<Text strong>How to use this section</Text>
							<div>1. Select the balance year first.</div>
							<div>2. Use Apply Defaults to initialize staff balances.</div>
							<div>
								3. Use Sync Settings to refresh existing records from leave
								settings.
							</div>
						</div>,
					)}
				>
					<Row gutter={[24, 12]} align='middle'>
						<Col xs={24} sm={8} md={5}>
							<Text
								type='secondary'
								style={{ fontSize: 12, display: 'block', marginBottom: 6 }}
							>
								BALANCE YEAR
							</Text>
							<Select
								value={year}
								onChange={setYear}
								options={YEARS.map((y) => ({ value: y, label: String(y) }))}
								style={{ width: '100%' }}
								size='large'
							/>
						</Col>
						<Col xs={24} sm={16} md={19}>
							<Text
								type='secondary'
								style={{ fontSize: 12, display: 'block', marginBottom: 6 }}
							>
								BULK ACTIONS
							</Text>
							<Space wrap>
								<Popconfirm
									title={`Apply default entitlements to ALL staff for ${year}?`}
									description='New staff will get default entitlements. Existing custom values are preserved.'
									onConfirm={handleBulkApply}
									okText='Apply'
									cancelText='Cancel'
								>
									<Button
										icon={<ThunderboltOutlined />}
										loading={bulkLoading}
										type='primary'
										ghost
									>
										Apply Defaults to All Staff ({year})
									</Button>
								</Popconfirm>
								<Popconfirm
									title={`Sync leave-type settings to all existing balance docs for ${year}?`}
									description='This will overwrite entitlement values with the latest defaults from Leave Type Settings.'
									onConfirm={handleSyncDefaults}
									okText='Sync'
									cancelText='Cancel'
								>
									<Button icon={<SyncOutlined />} loading={syncLoading}>
										Sync Settings to Existing ({year})
									</Button>
								</Popconfirm>
							</Space>
						</Col>
					</Row>
				</Card>

				{/* ── Section 2: Bulk Download / Upload ── */}
				<Card
					style={{
						marginBottom: 16,
						borderRadius: 8,
						boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
					}}
					styles={{ body: { padding: '16px 20px' } }}
					title={<Text strong>Bulk Download / Upload</Text>}
					extra={cardHelpIcon(
						<div style={{ maxWidth: 320 }}>
							<Text strong>How to use this section</Text>
							<div>1. Pick year and optional department to export balances.</div>
							<div>2. Download template before preparing upload data.</div>
							<div>3. Upload a completed file to bulk update staff balances.</div>
						</div>,
					)}
				>
					{/* Export row */}
					<Row gutter={[16, 12]} align='bottom'>
						<Col xs={24} md={6}>
							<Text
								type='secondary'
								style={{ fontSize: 12, display: 'block', marginBottom: 6 }}
							>
								YEAR
							</Text>
							<Select
								value={exportYear}
								onChange={setExportYear}
								options={YEARS.map((y) => ({ value: y, label: String(y) }))}
								style={{ width: '100%' }}
							/>
						</Col>
						<Col xs={24} md={8}>
							<Text
								type='secondary'
								style={{ fontSize: 12, display: 'block', marginBottom: 6 }}
							>
								DEPARTMENT (OPTIONAL)
							</Text>
							<Select
								allowClear
								placeholder='All departments'
								value={exportDepartmentId}
								onChange={setExportDepartmentId}
								options={departments}
								style={{ width: '100%' }}
							/>
						</Col>
						<Col xs={24} md={10}>
							<Space wrap>
								<Button
									icon={<DownloadOutlined />}
									type='primary'
									ghost
									loading={exportLoading}
									onClick={handleExportDownload}
								>
									Download Balance ({exportYear})
								</Button>
								<Button
									icon={<DownloadOutlined />}
									onClick={handleDownloadTemplate}
								>
									Download Template
								</Button>
							</Space>
						</Col>
					</Row>

					<Divider style={{ margin: '16px 0' }} />

					{/* Upload row */}
					<Row gutter={[16, 12]} align='middle'>
						<Col xs={24} md={16}>
							<Text type='secondary' style={{ fontSize: 13 }}>
								Upload a completed template file to bulk-update staff leave
								balances. Download the template first to ensure the correct column
								format.
							</Text>
						</Col>
						<Col xs={24} md={8} style={{ textAlign: 'right' }}>
							<input
								ref={fileInputRef}
								type='file'
								accept='.xlsx,.xls,.csv'
								style={{ display: 'none' }}
								onChange={handleFileChange}
							/>
							<Button
								icon={<UploadOutlined />}
								onClick={() => fileInputRef.current?.click()}
							>
								Upload Balance File (.xlsx / .csv)
							</Button>
						</Col>
					</Row>
				</Card>

				{/* ── Section 3: View / Edit Staff Balance ── */}
				<Card
					style={{ borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
					styles={{ body: { padding: '16px 20px' } }}
					title={<Text strong>View / Edit Staff Balance</Text>}
					extra={cardHelpIcon(
						<div style={{ maxWidth: 320 }}>
							<Text strong>How to use this section</Text>
							<div>1. Search staff by name or by staff ID.</div>
							<div>2. Review entitlement, used, and remaining days in the table.</div>
							<div>
								3. Click Edit Entitlements to update values for the selected year.
							</div>
						</div>,
					)}
				>
					{/* Search row */}
					<Row gutter={[16, 12]} align='bottom'>
						<Col xs={24} md={11}>
							<Text
								type='secondary'
								style={{ fontSize: 12, display: 'block', marginBottom: 6 }}
							>
								SEARCH BY NAME
							</Text>
							<AutoComplete
								options={staffOptions}
								value={staffSearch}
								onSearch={handleStaffSearch}
								onSelect={(_value, option: any) => {
									setStaffSearch(option.value)
									setSelectedStaffId(option.staffId)
									setStaffIdInput('')
								}}
								onClear={() => {
									setStaffSearch('')
									setSelectedStaffId(null)
									setBalance(null)
									setStaffOptions([])
								}}
								allowClear
								placeholder='Search by name...'
								style={{ width: '100%' }}
							/>
						</Col>
						<Col xs={24} md={11}>
							<Text
								type='secondary'
								style={{ fontSize: 12, display: 'block', marginBottom: 6 }}
							>
								OR SEARCH BY STAFF ID
							</Text>
							<Input.Search
								placeholder='Enter Staff ID...'
								value={staffIdInput}
								allowClear
								enterButton='Load'
								onChange={(e) => {
									const val = e.target.value
									setStaffIdInput(val)
									if (!val.trim()) {
										setSelectedStaffId(null)
										setBalance(null)
									}
								}}
								onSearch={(val) => {
									const id = val.trim()
									if (!id) return
									setStaffSearch('')
									setStaffOptions([])
									setSelectedStaffId(id)
								}}
								style={{ width: '100%' }}
							/>
						</Col>
					</Row>

					{/* Empty state */}
					{!balance && !loading && (
						<div
							style={{
								textAlign: 'center',
								padding: '48px 0 32px',
								color: '#9ca3af',
							}}
						>
							<Text type='secondary'>
								Search for a staff member above to view their leave balance.
							</Text>
						</div>
					)}

					{/* Balance result */}
					{balance && (
						<>
							{/* Staff info header */}
							<div
								style={{
									margin: '20px 0 16px',
									padding: '14px 18px',
									background: '#f8fafc',
									borderRadius: 8,
									border: '1px solid #e2e8f0',
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
									flexWrap: 'wrap',
									gap: 12,
								}}
							>
								<Space size={32} wrap>
									<div>
										<Text
											type='secondary'
											style={{
												fontSize: 11,
												display: 'block',
												marginBottom: 2,
											}}
										>
											STAFF ID
										</Text>
										<Text strong style={{ fontSize: 15 }}>
											{balance.staffId}
										</Text>
									</div>
									{balance.name && (
										<div>
											<Text
												type='secondary'
												style={{
													fontSize: 11,
													display: 'block',
													marginBottom: 2,
												}}
											>
												NAME
											</Text>
											<Text strong style={{ fontSize: 15 }}>
												{balance.name}
											</Text>
										</div>
									)}
									<div>
										<Text
											type='secondary'
											style={{
												fontSize: 11,
												display: 'block',
												marginBottom: 2,
											}}
										>
											YEAR
										</Text>
										<Text strong style={{ fontSize: 15 }}>
											{balance.year}
										</Text>
									</div>
								</Space>
								<Space>
									<Button
										icon={<ReloadOutlined />}
										onClick={fetchBalance}
										loading={loading}
									>
										Refresh
									</Button>
									<Button
										type='primary'
										icon={<EditOutlined />}
										onClick={handleOpenEdit}
									>
										Edit Entitlements
									</Button>
								</Space>
							</div>

							{/* Balance table */}
							<Table
								dataSource={balanceRows}
								columns={columns}
								rowKey='leaveType'
								pagination={false}
								size='middle'
								style={{ borderRadius: 8, overflow: 'hidden' }}
							/>
						</>
					)}
				</Card>
			</div>

			{/* Edit entitlements modal */}
			<Modal
				title={
					<span>
						Edit Leave Entitlements
						{balance && (
							<span
								style={{
									fontSize: 13,
									fontWeight: 400,
									marginLeft: 10,
									color: '#64748b',
								}}
							>
								{balance.name ? `${balance.name} — ` : ''}
								ID: {balance.staffId} — {balance.year}
							</span>
						)}
					</span>
				}
				open={editModalOpen}
				onCancel={() => setEditModalOpen(false)}
				onOk={handleSaveEntitlements}
				confirmLoading={saving}
				okText='Save'
			>
				{Object.keys(editValues).map((type) => (
					<Row key={type} gutter={[16, 8]} align='middle' style={{ marginBottom: 8 }}>
						<Col xs={12}>
							<Text>{type}</Text>
						</Col>
						<Col xs={12}>
							<InputNumber
								min={0}
								max={365}
								value={editValues[type]}
								onChange={(val) =>
									setEditValues((prev) => ({ ...prev, [type]: val ?? 0 }))
								}
								style={{ width: '100%' }}
								addonAfter='days'
							/>
						</Col>
					</Row>
				))}
			</Modal>

			{/* Import confirmation modal */}
			<Modal
				title='Confirm Bulk Import'
				open={importModalOpen}
				onCancel={() => {
					setImportModalOpen(false)
					setImportRecords([])
				}}
				onOk={handleConfirmImport}
				confirmLoading={importLoading}
				okText='Import'
			>
				<Space direction='vertical' style={{ width: '100%' }}>
					<Text>
						<strong>{importRecords.length}</strong> staff record(s) detected in the
						file.
					</Text>
					<Text type='secondary'>Select the year these balances apply to:</Text>
					<Select
						value={importYear}
						onChange={setImportYear}
						options={YEARS.map((y) => ({ value: y, label: String(y) }))}
						style={{ width: 160 }}
					/>
					<Text type='secondary'>
						This will overwrite existing entitlement values for each staff in the file.
					</Text>
				</Space>
			</Modal>
		</div>
	)
}

export default ManageBalance
